import type { ProblemDetail } from "@/types/api";
import { clientBuildHeaders } from "@/lib/buildInfo";
import { browserSupportsNativeHls } from "@/lib/nativeHls";

const BASE_URL = "/api/v1";
const REQUEST_TIMEOUT_MS = 8000;
const API_UNAVAILABLE_MESSAGE = "API not responding. It may still be starting up. Please try again in a few seconds.";
type QueryParamValue = string | number | boolean | null | undefined;
type RequestOptions = {
  timeoutMs?: number;
};
type GetRequestOptions = RequestOptions & {
  unavailableRetries?: number;
  retryDelayMs?: number;
};
export type FormUploadProgress = {
  loaded: number;
  total: number | null;
  percent: number | null;
  phase: "upload" | "processing";
};
type FormRequestOptions = RequestOptions & {
  onUploadProgress?: (progress: FormUploadProgress) => void;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public problem: ProblemDetail,
  ) {
    super(problem.detail || problem.title);
    this.name = "ApiError";
  }
}

export class ApiUnavailableError extends Error {
  constructor(public status: number = 0) {
    super(API_UNAVAILABLE_MESSAGE);
    this.name = "ApiUnavailableError";
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if ([502, 503, 504].includes(response.status)) {
      throw new ApiUnavailableError(response.status);
    }

    let problem: ProblemDetail | null = null;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("json")) {
      try {
        problem = await response.json() as ProblemDetail;
      } catch {
        problem = null;
      }
    }
    if (!problem) {
      problem = {
        type: "about:blank",
        title: response.statusText || "Request Failed",
        status: response.status,
        detail: response.statusText || "The request failed.",
        instance: "",
        code: "request_failed",
        request_id: "",
      };
    }
    throw new ApiError(response.status, problem);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: statusText || "Request Failed",
    status,
    detail: statusText || "The request failed.",
    instance: "",
    code: "request_failed",
    request_id: "",
  };
}

async function request<T>(
  path: string,
  init: RequestInit,
  params?: object,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<T> {
  let response: Response;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    response = await fetch(buildUrl(path, params), {
      credentials: "same-origin",
      ...init,
      signal: controller.signal,
    });
  } catch {
    throw new ApiUnavailableError();
  } finally {
    window.clearTimeout(timeout);
  }
  return handleResponse<T>(response);
}

function requestFormWithProgress<T>(
  path: string,
  body: FormData,
  options: FormRequestOptions,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildUrl(path));
    xhr.withCredentials = true;
    if (options.timeoutMs !== undefined) {
      xhr.timeout = options.timeoutMs;
    }
    for (const [key, value] of Object.entries(formHeaders() as Record<string, string>)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (event) => {
      options.onUploadProgress?.(formUploadProgress(event));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (xhr.status === 204 || stringsAreBlank(xhr.responseText)) {
          resolve(undefined as T);
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(new ApiUnavailableError(xhr.status));
        }
        return;
      }
      reject(errorFromXHR(xhr));
    };
    xhr.onerror = () => reject(new ApiUnavailableError());
    xhr.ontimeout = () => reject(new ApiUnavailableError());
    xhr.onabort = () => reject(new ApiUnavailableError());
    xhr.send(body);
  });
}

function formUploadProgress(event: ProgressEvent): FormUploadProgress {
  const total = event.lengthComputable ? event.total : null;
  const percent = total && total > 0
    ? Math.min(100, Math.round((event.loaded / total) * 100))
    : null;
  return {
    loaded: event.loaded,
    total,
    percent,
    phase: total !== null && event.loaded >= total ? "processing" : "upload",
  };
}

function errorFromXHR(xhr: XMLHttpRequest): Error {
  if ([502, 503, 504].includes(xhr.status)) {
    return new ApiUnavailableError(xhr.status);
  }

  const contentType = xhr.getResponseHeader("content-type") ?? "";
  let problem: ProblemDetail | null = null;
  if (contentType.includes("json") && !stringsAreBlank(xhr.responseText)) {
    try {
      problem = JSON.parse(xhr.responseText) as ProblemDetail;
    } catch {
      problem = null;
    }
  }
  return new ApiError(xhr.status, problem ?? fallbackProblem(xhr.status, xhr.statusText));
}

function stringsAreBlank(value: string): boolean {
  return value.trim() === "";
}

function buildUrl(
  path: string,
  params?: object,
): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params as Record<string, QueryParamValue>)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function detectDeviceName(): string {
  if (typeof navigator === "undefined") return "Browser";
  const ua = navigator.userAgent || "";
  // iPadOS 13+ Safari masquerades as Mac. Capability-detect via touch points + native HLS.
  const isIPadOSDesktopMode = /Macintosh/.test(ua)
    && typeof navigator.maxTouchPoints === "number"
    && navigator.maxTouchPoints > 1;
  if (/iPad/.test(ua) || isIPadOSDesktopMode) return "iPad";
  if (/iPhone|iPod/.test(ua)) return "iPhone";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh|Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return "Browser";
}

let cachedDeviceName: string | null = null;

function headers(extra?: Record<string, string>): HeadersInit {
  if (cachedDeviceName === null) cachedDeviceName = detectDeviceName();
  return {
    "Content-Type": "application/json",
    "X-KnotFin-Client-Name": "KnotFin Web",
    "X-KnotFin-Device-Name": cachedDeviceName,
    "X-KnotFin-Native-HLS-Support": browserSupportsNativeHls() ? "1" : "0",
    ...clientBuildHeaders(),
    ...extra,
  };
}

function formHeaders(): HeadersInit {
  if (cachedDeviceName === null) cachedDeviceName = detectDeviceName();
  return {
    "X-KnotFin-Client-Name": "KnotFin Web",
    "X-KnotFin-Device-Name": cachedDeviceName,
    "X-KnotFin-Native-HLS-Support": browserSupportsNativeHls() ? "1" : "0",
    ...clientBuildHeaders(),
  };
}

export async function get<T>(
  path: string,
  params?: object,
  options: GetRequestOptions = {},
): Promise<T> {
  let retriesRemaining = Math.max(0, options.unavailableRetries ?? 0);
  while (true) {
    try {
      return await request<T>(path, {
        method: "GET",
        headers: headers(),
      }, params, options.timeoutMs);
    } catch (error) {
      if (!(error instanceof ApiUnavailableError) || retriesRemaining === 0) {
        throw error;
      }
      retriesRemaining -= 1;
      if (options.retryDelayMs && options.retryDelayMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, options.retryDelayMs));
      }
    }
  }
}

export async function post<T>(
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: headers(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }, undefined, options.timeoutMs);
}

export async function postForm<T>(
  path: string,
  body: FormData,
  options: FormRequestOptions = {},
): Promise<T> {
  if (options.onUploadProgress) {
    return requestFormWithProgress<T>(path, body, options);
  }
  return request<T>(path, {
    method: "POST",
    headers: formHeaders(),
    body,
  }, undefined, options.timeoutMs);
}

export async function patch<T>(
  path: string,
  body: unknown,
): Promise<T> {
  return request<T>(path, {
    method: "PATCH",
    headers: headers(),
    body: JSON.stringify(body),
  });
}

export async function del<T = void>(path: string): Promise<T> {
  return request<T>(path, {
    method: "DELETE",
    headers: headers(),
  });
}

export function artworkUrl(
  itemId: string,
  type: string,
  opts?: { width?: number; format?: string; quality?: number; context?: string },
): string {
  const params: Record<string, string | number | undefined> = {
    width: opts?.width,
    format: opts?.format,
    quality: opts?.quality,
    context: opts?.context,
  };
  return buildUrl(`/items/${itemId}/artwork/${type}`, params);
}

export function eventSource(): EventSource {
  return new EventSource(buildUrl("/events"));
}
